import type { MotionPoint, MotionSeries, MotionWindow } from "./motionSeries";

/** 지금 그릴 계열 하나. `preview` 면 개요의 성긴 표본이다. 상세가 오면 바뀐다. */
export type MotionSeriesPick = { series: MotionSeries; preview: boolean };

/** (채널, 지표) 하나를 바로 찾는 색인. 채널 카드는 자기 계열만 가져오므로 지표별 전체 탐색은 불필요하다. */
export type MotionSeriesSource = ReadonlyMap<string, MotionSeriesPick>;

export function motionSeriesKey(channel: string, metric: string): string {
  // 채널 이름에 `::` 가 있어도 지표와 섞이지 않도록 제어문자로 구분한다.
  return `${channel}\u0000${metric}`;
}

/**
 * 지금 그릴 계열. **(채널, 지표)마다** 상세가 있으면 개요 대신 상세를 쓴다.
 *
 * 개요 JSON 은 모든 채널의 곡선을 이미 가진다(창원 실측 1.4 MB, 계열당 1,700점). 상세는 그
 * 10배다. 상세를 모두 받을 때까지 빈 화면을 보여 줄 이유가 없다. 그래서 개요로 먼저 그리고,
 * 상세가 도착하면 바꾼다. 상세에 어떤 계열이 없으면 그 계열만 개요로 남는다.
 */
export function motionSeriesSource(
  overview: readonly MotionSeries[],
  detail: readonly MotionSeries[],
): MotionSeriesSource {
  const source = new Map<string, MotionSeriesPick>();
  for (const series of overview) {
    source.set(motionSeriesKey(series.channel, series.metric), { series, preview: true });
  }
  for (const series of detail) {
    source.set(motionSeriesKey(series.channel, series.metric), { series, preview: false });
  }
  return source;
}

const cache = new WeakMap<
  readonly MotionPoint[],
  { key: string; points: readonly MotionPoint[] }
>();

/** 원본을 보존하고 현재 창의 픽셀별 첫·끝·최소·최대를 표시한다. 결측 사이를 연결하지 않는다. */
export function sampleMotionPoints(
  points: readonly MotionPoint[],
  window: MotionWindow,
  pixels: number,
): readonly MotionPoint[] {
  const width = Math.max(1, Math.ceil(pixels));
  const key = `${window.start}:${window.end}:${width}`;
  const saved = cache.get(points);
  if (saved?.key === key) return saved.points;
  if (!points.length || window.end <= window.start) return [];
  // 창 양 옆으로 한 점씩 더 가져온다. 경계를 가로지르는 선을 그리려면 바깥 끝점이 필요하다.
  const start = Math.max(0, lowerBound(points, window.start, false) - 1);
  const stop = Math.min(points.length, lowerBound(points, window.end, true) + 1);
  const sampled: MotionPoint[] = [];
  let first = -1,
    last = -1,
    min = -1,
    max = -1,
    bucket = -1;
  const flush = () => {
    if (first < 0) return;
    const keep = new Set([first, min, max, last]);
    // 남기는 절점의 같은 시각 양쪽 값을 보존한다. 동일 값 중복은 늘리지 않는다.
    for (const i of [...keep]) {
      for (const j of [i - 1, i + 1]) {
        if (
          j >= first &&
          j <= last &&
          points[j]!.t === points[i]!.t &&
          points[j]!.v !== points[i]!.v
        )
          keep.add(j);
      }
    }
    for (const i of [...keep].sort((a, b) => a - b)) sampled.push(points[i]!);
    first = -1;
  };
  for (let i = start; i < stop; i++) {
    const p = points[i]!;
    if (p.v === null) {
      flush();
      if (sampled.length && sampled[sampled.length - 1]!.v !== null) sampled.push(p);
      continue;
    }
    const nextBucket = Math.floor(((p.t - window.start) / (window.end - window.start)) * width);
    if (first >= 0 && bucket !== nextBucket) flush();
    if (first < 0) {
      first = min = max = i;
      bucket = nextBucket;
    }
    last = i;
    if (p.v < points[min]!.v!) min = i;
    if (p.v > points[max]!.v!) max = i;
  }
  flush();
  // 경계 밖 점의 값이 확대 구간의 y축을 늘리지 않도록 교차 지점을 보간한다.
  for (const [i, j, edge] of [
    [0, 1, window.start],
    [sampled.length - 1, sampled.length - 2, window.end],
  ]) {
    const p = sampled[i!],
      next = sampled[j!];
    if (
      p &&
      next &&
      p.v !== null &&
      next.v !== null &&
      p.t !== next.t &&
      ((i === 0 && p.t < edge!) || (i !== 0 && p.t > edge!))
    ) {
      sampled[i!] = { t: edge!, v: p.v + ((next.v - p.v) * (edge! - p.t)) / (next.t - p.t) };
    }
  }
  const clipped = sampled.filter((point) => point.t >= window.start && point.t <= window.end);
  cache.set(points, { key, points: clipped });
  return clipped;
}

/** `points` 에서 `time` 이 들어갈 위치. `inclusive` 면 같은 시각의 점 뒤 위치를 돌려준다. */
function lowerBound(points: readonly MotionPoint[], time: number, inclusive: boolean): number {
  let lo = 0,
    hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (points[mid]!.t < time || (inclusive && points[mid]!.t === time)) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// 분포를 계산할 때 쓰는 히스토그램 칸 수. 상세 한 채널은 백만 점을 넘을 수 있다. 표본을
// 정렬하면 줌마다 수백 ms 가 걸린다. 칸 세기는 표본을 한 번만 순회한다. y축 눈금에는 이
// 해상도로 충분하다.
const DISTRIBUTION_BINS = 8192;

// 주요 분포 상단(p95)과 중앙값의 거리에 곱하는 배수. 이 배수까지를 "분포 안"으로 본다.
// 로컬 fixture 두 개의 speed·acceleration 계열 576개에서 측정했다. 4 는 39%, 8 은 12%, 12 는
// 10% 계열에서 발동했다. 8 일 때 발동한 계열의 실제 최댓값은 경계의 약 5배였다. 그래서 8 은
// 실제 극단값에서만 발동한다. 값을 낮추면 정상 에피소드의 y축도 바뀐다.
const OUTLIER_SPREAD_MULTIPLE = 8;

// 표본이 이보다 적으면 분포를 판단할 수 없다. 그러면 y축을 바꾸지 않는다.
const MIN_DISTRIBUTION_SAMPLES = 8;

// y축을 좁혀서 이 비율 이상 커질 때만 y축을 바꾼다. fixture 144개 차트에서 좁히는 차트의
// 약 1/4 은 1.5배 미만이었다. 그 정도로는 가독성이 나아지지 않는다. y축 눈금만 낯설어지고
// 배지가 하나 더 표시된다.
const MIN_CLIP_RATIO = 1.5;

const boundCache = new WeakMap<readonly MotionPoint[], { key: string; bound: number | null }>();

/**
 * 창 안 분포의 상단 경계. 이보다 큰 값은 아웃라이어로 본다. 판단할 수 없으면 `null`.
 *
 * `중앙값 + 8×(p95 − 중앙값)`. 표본은 **인접 간격으로 가중**한다. 기록 시간 간격은 고르지
 * 않다(보통 5ms, 피크 주변에는 0.2ms 구간이 모인다). 점 수로 세면 짧은 순간에 모인 표본이
 * 분포를 치우치게 한다.
 *
 * 이 함수는 값을 지우거나 고치지 않는다. y축을 끊을 위치만 정한다. 호출부는 실제 최댓값을 따로
 * 보관하고 그대로 보여 줘야 한다.
 */
export function robustUpperBound(
  points: readonly MotionPoint[],
  window: MotionWindow,
): number | null {
  const key = `${window.start}:${window.end}`;
  const saved = boundCache.get(points);
  if (saved?.key === key) return saved.bound;
  const bound = computeRobustUpperBound(points, window);
  boundCache.set(points, { key, bound });
  return bound;
}

function computeRobustUpperBound(
  points: readonly MotionPoint[],
  window: MotionWindow,
): number | null {
  if (!points.length || window.end <= window.start) return null;
  const start = lowerBound(points, window.start, false);
  const stop = lowerBound(points, window.end, true);
  let min = Infinity,
    max = -Infinity,
    count = 0;
  for (let i = start; i < stop; i++) {
    const v = points[i]!.v;
    if (v === null) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    count++;
  }
  // 표본이 적거나 값이 하나뿐이면 끊을 위치가 없다.
  if (count < MIN_DISTRIBUTION_SAMPLES || max <= min) return null;

  const weights = new Float64Array(DISTRIBUTION_BINS);
  const span = max - min;
  // 표본 하나의 가중치 상한. 간격을 그대로 가중치로 쓰면 **큰 틈 뒤의 단독 표본이 그 틈
  // 전체의 가중치를 가진다**. 테스트에서 결측 구간 건너편의 단독 피크 하나가 전체 가중치의
  // 20% 를 가져가 분포 전체를 치우치게 했다. 그래서 상한을 평균 간격의 두 배로 둔다.
  const extent = points[stop - 1]!.t - points[start]!.t;
  const weightCap = extent > 0 ? (2 * extent) / count : Infinity;
  let total = 0;
  for (let i = start; i < stop; i++) {
    const point = points[i]!;
    if (point.v === null) continue;
    // 양쪽 인접 간격의 절반씩을 이 표본의 가중치로 쓴다(양 끝 표본은 한쪽만).
    const previous = i > start ? points[i - 1]!.t : point.t;
    const next = i + 1 < stop ? points[i + 1]!.t : point.t;
    const weight = Math.min((next - previous) / 2, weightCap);
    if (!(weight > 0)) continue;
    weights[
      Math.min(DISTRIBUTION_BINS - 1, Math.floor(((point.v - min) / span) * DISTRIBUTION_BINS))
    ] += weight;
    total += weight;
  }
  if (total <= 0) return null;

  const median = weightedQuantile(weights, total, 0.5, min, span);
  const p95 = weightedQuantile(weights, total, 0.95, min, span);
  const spread = p95 - median;
  // 분포가 한 칸에 모여 있으면(상수에 가까움) 경계가 중앙값과 같아져 정상 값도 잘린다.
  if (!(spread > 0)) return null;
  return median + OUTLIER_SPREAD_MULTIPLE * spread;
}

/** 누적 가중치가 `q` 를 넘는 칸의 값. 칸 가운데를 대표값으로 쓴다. */
function weightedQuantile(
  weights: Float64Array,
  total: number,
  q: number,
  min: number,
  span: number,
): number {
  const target = q * total;
  let accumulated = 0;
  for (let bin = 0; bin < weights.length; bin++) {
    accumulated += weights[bin]!;
    if (accumulated >= target) return min + ((bin + 0.5) / weights.length) * span;
  }
  return min + span;
}

/** 실제 최댓값의 위치. y축을 낮춰도 이 값과 시각은 그대로 보여 준다. */
export type MotionAxisMaximum = { channel: string; t: number; value: number };

export type MotionAxis = {
  /** 그릴 y축의 아래·위 끝. 아래 끝은 항상 0 을 포함한다. */
  min: number;
  max: number;
  /** 표시 채널의 실제 최댓값. `max` 보다 클 수 있다. */
  actualMax: number;
  /** y축이 지금 실제 최댓값보다 낮은지 여부. 참이면 아웃라이어가 y축 범위 밖에 있다. */
  clipped: boolean;
  /** 주요 분포 y축이 실제 최댓값보다 낮은지 여부. `fullRange` 로 펼친 동안에도 참이다. 그래서
   *  되돌리는 토글의 표시 여부를 이 값으로 정한다. */
  outlying: boolean;
  maximum: MotionAxisMaximum | null;
  /** 창 안 유효 표본 수. 0 이면 그릴 표본이 없다. */
  count: number;
};

/**
 * 한 지표 차트의 y축. 여러 계열을 함께 받아 **좌우 열이 같은 y축을 쓰게** 한다.
 *
 * 기본값은 주요 분포에 맞춘 y축이다. 실제 최댓값이 `robustUpperBound` 의 경계를 넘을 때만
 * y축을 낮춘다(`clipped`). `fullRange` 면 항상 실제 최댓값까지 펼친다.
 *
 * 분포를 판단할 수 없는 계열(표본 부족·상수)은 **자기 최댓값을 경계로 쓴다**. 판단할 수 없는
 * 채널을 잘라내면 정상 값이 y축 범위 밖으로 나간다. 실측에서 이 규칙으로 ep000000 속력 차트의
 * y축이 1.36 으로 좁혀졌고, obs 채널(최댓값 1.03)은 모두 범위 안에 남았다.
 */
export function motionAxis(
  series: readonly MotionSeries[],
  window: MotionWindow,
  fullRange: boolean,
): MotionAxis {
  let min = 0,
    actualMax = 0,
    count = 0,
    bound = 0,
    bounded = false;
  let maximum: MotionAxisMaximum | null = null;
  for (const item of series) {
    const start = lowerBound(item.points, window.start, false);
    const stop = lowerBound(item.points, window.end, true);
    let seriesMax = -Infinity,
      seriesCount = 0;
    for (let i = start; i < stop; i++) {
      const point = item.points[i]!;
      if (point.v === null) continue;
      seriesCount++;
      if (point.v < min) min = point.v;
      if (point.v > seriesMax) seriesMax = point.v;
      if (maximum === null || point.v > maximum.value) {
        maximum = { channel: item.channel, t: point.t, value: point.v };
      }
    }
    if (!seriesCount) continue;
    count += seriesCount;
    if (seriesMax > actualMax) actualMax = seriesMax;
    // 계열의 경계는 자기 최댓값을 넘지 않는다. 그 위에는 정보가 없다. 아웃라이어가 없는 계열의
    // 넓은 경계가 차트의 y축 전체를 올리지 않게 한다. 분포를 판단할 수 없는 계열(null)은 자기
    // 최댓값을 쓴다.
    const seriesBound = Math.min(robustUpperBound(item.points, window) ?? seriesMax, seriesMax);
    if (seriesBound > bound) bound = seriesBound;
    bounded = true;
  }
  const outlying = bounded && bound > 0 && actualMax >= bound * MIN_CLIP_RATIO;
  const max = fullRange || !outlying ? actualMax : bound;
  return { min, max, actualMax, clipped: max < actualMax, outlying, maximum, count };
}
